import { useBreakpointValue } from '@chakra-ui/react';
import { FC, PropsWithChildren, RefObject, useCallback, useEffect, useRef, useState } from 'react';

import {
	Box,
	BoxProps,
	ChevronLeftOutline,
	Flex,
	HStack,
	IconButton,
	InformationCircleOutline,
	Portal,
	rem,
	Text,
	Tooltip,
	XOutline,
} from '@bonusly/design-system';
import { useSpoofing } from '@bonusly/who-am-i';

const SPOOFING_BANNER_HEIGHT = 60;
const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 60;

interface PanelProps extends BoxProps {
	closeButtonLabel: string;
	/** When true, clicking outside the panel will close it (desktop only) */
	closeOnClickOutside?: boolean;
	isOpen: boolean;
	isSidebarExpanded: boolean;
	onBack?: () => void;
	onClose: () => void;
	scrollRef?: RefObject<HTMLDivElement>;
	title: string;
	titleTooltip?: string;
}

export const Panel: FC<PropsWithChildren<PanelProps>> = ({
	children,
	closeButtonLabel,
	closeOnClickOutside = false,
	isOpen,
	isSidebarExpanded,
	onBack,
	onClose,
	scrollRef,
	title,
	titleTooltip,
	...props
}) => {
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const { isSpoofing, isDemoing } = useSpoofing();
	const isSpoofingBannerVisible = isSpoofing && !isDemoing;
	const sidebarWidth = isSidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;
	const isMobile = useBreakpointValue({ base: true, sm: false }) ?? true;
	const [isScrolled, setIsScrolled] = useState(false);

	// Internal scroll container ref for tracking scroll position
	const internalScrollRef = useRef<HTMLDivElement>(null);

	// Track scroll position to show/hide header fade
	const handleScroll = useCallback(() => {
		const element = scrollRef?.current ?? internalScrollRef.current;
		if (element) {
			setIsScrolled(element.scrollTop > 0);
		}
	}, [scrollRef]);

	// Attach scroll listener
	useEffect(() => {
		const element = scrollRef?.current ?? internalScrollRef.current;
		if (element) {
			element.addEventListener('scroll', handleScroll);
			// Check initial scroll position
			handleScroll();
			return () => element.removeEventListener('scroll', handleScroll);
		}
	}, [scrollRef, handleScroll]);

	// Focus the close button when panel opens
	useEffect(() => {
		if (isOpen) {
			requestAnimationFrame(() => {
				closeButtonRef.current?.focus();
			});
		}
	}, [isOpen]);

	if (!isOpen) {
		return null;
	}

	return (
		<Portal>
			{/* Invisible backdrop to detect clicks outside - only when enabled and on desktop */}
			{/* Uses same z-index as modal but renders before panel in DOM, so panel appears above it */}
			{closeOnClickOutside && !isMobile && <Box position="fixed" top={0} left={`${sidebarWidth}px`} right={0} bottom={0} zIndex="modal" onClick={onClose} />}
			<Flex
				position="fixed"
				top={isSpoofingBannerVisible ? `${SPOOFING_BANNER_HEIGHT + 8}px` : rem(8)}
				left={`${sidebarWidth + 8}px`}
				bottom={rem(8)}
				right={undefined}
				bg="bg.minipage"
				backdropFilter="blur(44px)"
				border={'1px solid'}
				borderColor={'border.default'}
				borderRadius={rem(20)}
				zIndex="modal"
				overflow="hidden"
				boxShadow={'base'}
				px={rem(8)}
				h={undefined}
				flexDirection="column"
				{...props}
				w={props.width ?? '100%'}
				// NOTE: this is below props on purpose so we can add mobile styles
				{...(isMobile && {
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					border: 'none',
					borderColor: 'transparent',
					borderRadius: 0,
					boxShadow: 'none',
					h: '100vh',
					w: '100vw',
				})}
			>
				{/* Fixed Header - doesn't scroll */}
				<Box flexShrink={0} pt={rem(20)} pb={rem(12)} px={rem(12)} position="relative">
					<HStack justify="space-between" align="center">
						{onBack ? (
							<Flex
								as="button"
								alignItems="center"
								gap={rem(4)}
								onClick={onBack}
								cursor="pointer"
								_hover={{ opacity: 0.8 }}
								bg="transparent"
								border="none"
								p={0}
							>
								<ChevronLeftOutline boxSize={rem(24)} color="text.default" />
								<Text fontSize={200} fontWeight="semibold">
									{title}
								</Text>
							</Flex>
						) : (
							<Flex alignItems="center" gap={rem(6)}>
								<Text fontSize={200} fontWeight="semibold">
									{title}
								</Text>
								{titleTooltip && (
									<Tooltip label={titleTooltip} placement="bottom">
										<Box as="span" display="inline-flex" color="info.icon">
											<InformationCircleOutline boxSize={rem(20)} />
										</Box>
									</Tooltip>
								)}
							</Flex>
						)}
						<IconButton
							ref={closeButtonRef}
							aria-label={closeButtonLabel}
							icon={<XOutline />}
							size="sm"
							bg="secondary.bgAlpha"
							borderRadius="full"
							onClick={onClose}
							_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', borderRadius: 'full', outlineOffset: '-2px' }}
							data-testid="panel-close"
						/>
					</HStack>
					{/* Scroll fade gradient - shows when content is scrolled */}
					<Box
						position="absolute"
						left={0}
						right={0}
						bottom={0}
						height={rem(28)}
						transform="translateY(100%)"
						background="linear-gradient(to bottom, var(--chakra-colors-bg-minipage) 0%, transparent 100%)"
						pointerEvents="none"
						opacity={isScrolled ? 1 : 0}
						transition="opacity 0.15s ease-in-out"
						zIndex={1}
					/>
				</Box>

				{/* Scrollable Content */}
				<Box ref={scrollRef ?? internalScrollRef} flex={1} overflowY="auto" pb={rem(20)}>
					{children}
				</Box>
			</Flex>
		</Portal>
	);
};
